import React, { useState, useEffect, useMemo } from 'react';
import ProtectedRoute from '../../routes/protected';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Editor } from 'primereact/editor';
import { renderHeaderEditor } from '../../helpers/editorToolbar';
import { isRichTextEmpty } from '../../helpers/richText';
import { onSurveyDescriptionEditorLoad } from '../../helpers/surveyDescriptionEditor';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import surveyService from '../../services/data/survey.service';
import SurveyPageHeader from './SurveyPageHeader';

const FREQUENCY_OPTIONS = [
    { value: 'M', label: 'Mensal' },
    { value: 'B', label: 'Quinzenal' },
    { value: 'W', label: 'Semanal' },
    { value: '', label: 'Configurar disparos automáticos' },
];

const TARGET_OPTIONS = [
    { value: 'ALL', label: 'Todos colaboradores' },
    { value: 'LE', label: 'Apenas líderes' },
    { value: 'NL', label: 'Apenas não líderes' },
];

export default function SurveyCreate() {
    const navigate = useNavigate();
    const toast = React.useRef(null);
    const VerifyPerms = UsePerms();
    const { rootUser, company } = useCompanyData();
    const companyId = localStorage.getItem('companyId');
    const canCreateSurvey = VerifyPerms(['add_survey']) || rootUser;
    const canCreateTemplate = VerifyPerms(['add_surveytemplate']) || rootUser;
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        template: '',
        name: '',
        description: '',
        frequency: 'M',
        target_filter: 'ALL',
    });
    const headerEditor = useMemo(() => renderHeaderEditor(), []);

    function normalizeTemplateList(r) {
        if (Array.isArray(r)) return r;
        if (r && Array.isArray(r.results)) return r.results;
        return [];
    }

    function descriptionFromSelectedTemplate(templateId) {
        if (!templateId) return '';
        const t = templates.find((x) => String(x.id) === String(templateId));
        if (!t?.description || isRichTextEmpty(t.description)) return '';
        return t.description;
    }

    function handleTemplateChange(templateId) {
        setForm((prev) => ({
            ...prev,
            template: templateId,
            description: descriptionFromSelectedTemplate(templateId),
        }));
    }

    useEffect(() => {
        if (company?.id && !canCreateSurvey) {
            navigate('/survey/list/');
            return;
        }
        if (!companyId) return;
        surveyService.listTemplates({ company_id: companyId })
            .then((r) => {
                const withQuestions = normalizeTemplateList(r).filter(
                    (t) => (t.questions_count ?? 0) > 0
                );
                setTemplates(withQuestions);
            })
            .catch(() => setTemplates([]))
            .finally(() => setLoading(false));
    }, [companyId, company?.id, canCreateSurvey, navigate]);

    function handleSubmit(e) {
        e.preventDefault();
        if (templates.length === 0) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Template necessário',
                    detail:
                        'Não há templates disponíveis. Crie um template antes de criar a pesquisa.',
                });
            }
            return;
        }
        if (!form.template || !form.name) {
            if (toast.current) toast.current.show({ severity: 'warn', summary: 'Atenção', detail: 'Preencha template e nome' });
            return;
        }
        setSubmitting(true);
        surveyService.createSurvey({
            company_id: companyId,
            template: form.template,
            name: form.name,
            description: isRichTextEmpty(form.description) ? '' : form.description,
            frequency: form.frequency || null,
            target_filter: form.target_filter,
        })
            .then((data) => {
                const isAuto = !form.frequency;
                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Sucesso',
                        detail: isAuto
                            ? 'Pesquisa criada, ativada e com disparo automático habilitado. Configure os gatilhos.'
                            : 'Pesquisa criada',
                    });
                }
                if (data?.id) {
                    const tab = isAuto ? 'triggers' : 'indicators';
                    navigate(`/survey/dashboard/${data.id}/`, { state: { activeTab: tab } });
                } else {
                    navigate('/survey/list/');
                }
            })
            .catch(err => {
                if (toast.current) toast.current.show({ severity: 'error', summary: 'Erro', detail: err.response?.data?.detail || 'Falha ao criar' });
            })
            .finally(() => setSubmitting(false));
    }

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />
            <div className="container-fluid">
                <SurveyPageHeader title="Nova Pesquisa" />
                <div className="my-4 mx-5 py-3 rounded-top">
                    <div className="d-flex align-items-center mb-3">
                        <a href="/survey/list/" className="nav-link">
                            <span className="text-green fw-bold me-2 p-0">
                                <FontAwesomeIcon icon={solid('arrow-left')} /> Voltar à lista
                            </span>
                        </a>
                    </div>

                    <div className="bg-white rounded border-clean p-4" style={{minHeight: '500px', marginBottom: '200px'}}>
                        {company?.id && !canCreateSurvey ? (
                            <div className="alert alert-warning">Você não tem permissão para criar pesquisas.</div>
                        ) : loading ? (
                            <div className="text-center py-5 text-muted">Carregando templates...</div>
                        ) : templates.length === 0 ? (
                            <div className="alert alert-warning" role="alert">
                                <strong className="d-block mb-2">É preciso criar um template com perguntas primeiro</strong>
                                <p className="small mb-0">
                                    Toda pesquisa usa um template com ao menos uma pergunta. Cadastre um template e adicione perguntas
                                    antes de criar uma nova pesquisa.
                                </p>
                                {canCreateTemplate ? (
                                    <div className="mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-success btn-sm btn-rounded px-3"
                                            onClick={() => navigate('/survey/template/create/')}
                                        >
                                            <FontAwesomeIcon icon={solid('plus')} className="me-1" /> Criar template agora
                                        </button>
                                    </div>
                                ) : (
                                    <span className="d-block mt-2 small">
                                        Peça a um administrador para criar um template ou liberar a permissão de cadastro de templates.
                                    </span>
                                )}
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Template *</label>
                                    <div className="d-flex flex-wrap gap-2 align-items-center">
                                        <div className="flex-grow-1" style={{ minWidth: '220px' }}>
                                            <select
                                                className="form-select"
                                                value={form.template}
                                                onChange={(e) => handleTemplateChange(e.target.value)}
                                                required
                                            >
                                                <option value="">Selecione...</option>
                                                {templates.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name} {t.is_default ? '(padrão)' : ''}</option>
                                                ))}
                                            </select>
                                        </div>
                                        {canCreateTemplate && (
                                            <button
                                                type="button"
                                                className="btn btn-outline-success btn-sm btn-rounded px-3 text-nowrap"
                                                onClick={() => navigate('/survey/template/create/')}
                                            >
                                                <FontAwesomeIcon icon={solid('plus')} className="me-1" /> Novo template
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Nome da pesquisa *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={form.name}
                                        onChange={e => setForm({ ...form, name: e.target.value })}
                                        placeholder="Ex: Clima Q1 2025"
                                        required
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Descrição (opcional)</label>
                                    <Editor
                                        style={{ height: '200px' }}
                                        className="survey-description-editor"
                                        headerTemplate={headerEditor}
                                        value={form.description}
                                        onLoad={onSurveyDescriptionEditorLoad}
                                        onTextChange={(e) => setForm({ ...form, description: e.htmlValue ?? '' })}
                                    />
                                    <div className="form-text">
                                        Esse texto aparece na tela de pesquisa pendente, antes das perguntas. Ao escolher um template, a
                                        descrição dele é copiada aqui para você poder ajustar.
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Frequência de envio</label>
                                    <select
                                        className="form-select"
                                        value={form.frequency}
                                        onChange={e => setForm({ ...form, frequency: e.target.value })}
                                    >
                                        {FREQUENCY_OPTIONS.map(o => (
                                            <option key={o.value || 'auto'} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    {form.frequency ? (
                                        <div className="form-text">
                                            Ao <strong>ativar</strong>, define o intervalo de lembretes por e-mail/SMS entre início e término
                                            da campanha (quem ainda não respondeu).
                                        </div>
                                    ) : (
                                        <div className="form-text">
                                            A pesquisa será <strong>ativada automaticamente</strong> com disparo automático habilitado.
                                            Em seguida, configure os gatilhos no painel da pesquisa (ex.: 45 dias da admissão).
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Público-alvo</label>
                                    <select
                                        className="form-select"
                                        value={form.target_filter}
                                        onChange={e => setForm({ ...form, target_filter: e.target.value })}
                                    >
                                        {TARGET_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="d-flex gap-2">
                                    <button type="submit" className="btn btn-success btn-sm btn-rounded px-3" disabled={submitting}>
                                        {submitting ? 'Criando...' : 'Criar Pesquisa'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </ProtectedRoute>
    );
}
